import { useState } from "react";
import { useTranslation } from "react-i18next";
import { XCircle, Award, ChevronRight, TrendingUp } from "lucide-react";
import { formatMoney } from "../../lib/currency";
import MachineAlternativesDialog from "./MachineAlternativesDialog";

export default function OperationsTab({ q, c, editMode, overrides, setOverrides, onApplyMachine }) {
  const { t } = useTranslation();
  const [pickerOp, setPickerOp] = useState(null);
  const scoreColor = (s) => {
    if (s == null) return "text-slate-500";
    if (s >= 80) return "text-emerald-300";
    if (s >= 60) return "text-blue-300";
    if (s >= 40) return "text-amber-300";
    return "text-rose-300";
  };
  return (
    <div className="space-y-5" data-testid="operations-tab">
      <div className="tech-card overflow-hidden">
        <div className="px-5 py-4 border-b border-slate-700/60 flex items-center justify-between">
          <div className="label-tech">{t("quoteTabs.operationsList")}</div>
          <div className="text-xs text-slate-500 font-mono-data">{c.breakdown?.length || 0} step</div>
        </div>
        <div className="divide-y divide-slate-800">
          {c.breakdown?.map((b, i) => (
            <div key={`op-${i}-${b.operation}`} className="px-5 py-4" data-testid={`op-row-${i}`}>
              <div className="flex items-center gap-4">
              <div className={`w-2 h-2 rounded-full ${b.owned ? "bg-emerald-500" : b.outsource ? "bg-amber-500" : "bg-rose-500"}`}/>
              <div className="flex-1 min-w-0">
                <div className="font-display font-medium text-slate-100 flex items-center gap-2 flex-wrap">
                  {b.operation_label}
                  {b.overridden && (
                    <span className="text-[9px] label-tech px-1.5 py-0.5 rounded bg-blue-500/20 text-blue-300 border border-blue-500/40">MOD</span>
                  )}
                  {b.machine_overridden && (
                    <span className="text-[9px] label-tech px-1.5 py-0.5 rounded bg-violet-500/20 text-violet-300 border border-violet-500/40" data-testid={`machine-overridden-${i}`}>{t("quoteTabs.machineMod")}</span>
                  )}
                  {b.outsource && (
                    <span className="text-[9px] label-tech px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-300 border border-amber-500/40">OUTSOURCE</span>
                  )}
                  {b.match_score != null && (
                    <span
                      className={`text-[10px] font-mono-data flex items-center gap-1 ${scoreColor(b.match_score)}`}
                      title={b.match_why || ""}
                      data-testid={`match-score-${i}`}
                    >
                      <Award size={10}/> {b.match_score}/100
                    </span>
                  )}
                </div>
                <div className="text-xs text-slate-500 mt-0.5 truncate">
                  {b.machine_name}{b.notes ? ` · ${b.notes}` : ""}
                </div>
                {b.match_why && (
                  <div className="text-[11px] text-slate-400 mt-1 font-mono-data" data-testid={`match-why-${i}`}>
                    {b.match_why}
                  </div>
                )}
              </div>
              <div className="text-right flex items-center gap-3">
                {editMode ? (
                  <div className="flex items-center gap-1">
                    <input
                      type="number" step="0.1" min="0"
                      value={overrides[i] ?? ""}
                      onChange={(e) => setOverrides({ ...overrides, [i]: e.target.value })}
                      className="w-20 px-2 py-1 rounded text-sm font-mono-data text-right"
                      data-testid={`override-input-${i}`}
                    />
                    <span className="text-xs text-slate-500">min/pz</span>
                  </div>
                ) : (
                  <div>
                    <div className="font-mono-data text-sm text-slate-100">{(b.minutes_per_piece || 0).toFixed(2)}&apos;</div>
                    <div className="font-mono-data text-xs text-blue-400">{formatMoney(b.cost_per_piece_eur, q.currency)}/pz</div>
                  </div>
                )}
                {!editMode && (
                  <button
                    type="button"
                    onClick={() => setPickerOp({ type: b.operation, label: b.operation_label, index: i, row: b })}
                    className="p-1.5 rounded text-slate-400 hover:text-blue-300 hover:bg-blue-500/10 transition-colors"
                    title={t("quoteTabs.changeMachine")}
                    data-testid={`change-machine-${i}`}
                  >
                    <ChevronRight size={14}/>
                  </button>
                )}
              </div>
              </div>

              {/* "Vs ottima del mercato" — shown when the user's owned machine
                  was chosen, so they can see the precision/throughput gap vs
                  the market-leading machine for this operation. */}
              {b.owned && b.optimal_market && (
                <div className="mt-3 ml-6 pl-3 border-l border-cyan-500/30 bg-cyan-500/5 rounded-r-sm py-2 pr-3" data-testid={`optimal-market-${i}`}>
                  <div className="flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-wider text-cyan-300 mb-1">
                    <TrendingUp size={10}/> {t("quoteTabs.vsOptimalMarket")}
                  </div>
                  <div className="text-sm text-slate-200 font-medium">{b.optimal_market.machine_name}</div>
                  <div className="text-[11px] text-slate-400 mt-1 font-mono-data grid grid-cols-2 md:grid-cols-4 gap-x-3 gap-y-1">
                    <span>Prec. <span className="text-cyan-300">±{b.optimal_market.precision_um}µm</span></span>
                    {b.optimal_market.spindle_kw != null && (
                      <span>Mand. <span className="text-cyan-300">{b.optimal_market.spindle_kw} kW</span></span>
                    )}
                    {b.optimal_market.max_torque_nm != null && b.optimal_market.max_torque_nm > 0 && (
                      <span>Coppia <span className="text-cyan-300">{b.optimal_market.max_torque_nm} Nm</span></span>
                    )}
                    {b.optimal_market.roughing_capacity_cm3_min != null && (
                      <span>Sgr. <span className="text-cyan-300">{b.optimal_market.roughing_capacity_cm3_min} cm³/min</span></span>
                    )}
                  </div>
                  <div className="text-[11px] text-slate-500 mt-1 font-mono-data">
                    €{b.optimal_market.hourly_rate}/h · {t("quoteTabs.costLabel")} {formatMoney(b.optimal_market.cost_per_piece_eur, q.currency)}/pz
                    {b.optimal_market.delta_cost_eur != null && (
                      <span className={b.optimal_market.delta_cost_eur > 0 ? " text-rose-300" : " text-emerald-300"}>
                        {" "}({b.optimal_market.delta_cost_eur > 0 ? "+" : ""}{formatMoney(b.optimal_market.delta_cost_eur, q.currency)}/pz)
                      </span>
                    )}
                    {b.optimal_market.delta_precision_um != null && b.optimal_market.delta_precision_um > 0 && (
                      <span className="text-emerald-300"> · +{b.optimal_market.delta_precision_um}µm {t("quoteTabs.precisionGain")}</span>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      <MachineAlternativesDialog
        open={!!pickerOp}
        onOpenChange={(v) => !v && setPickerOp(null)}
        quoteId={q.id}
        operationType={pickerOp?.type}
        operationLabel={pickerOp?.label}
        opIndex={pickerOp?.index}
        currentRow={pickerOp?.row}
        currency={q.currency}
        fxRate={c.totals?.fx_rate_from_eur || 1}
        onApply={onApplyMachine}
      />

      {c.missing_operations?.length > 0 && (
        <div className="tech-card p-5 border-amber-500/30" data-testid="missing-ops">
          <div className="flex items-center gap-2 mb-3 text-amber-400">
            <XCircle size={16}/> <div className="label-tech text-amber-400">{t("quoteTabs.missingOpsTitle")}</div>
          </div>
          <p className="text-sm text-slate-400 mb-4">{t("quoteTabs.missingOpsBody")}</p>
          <div className="space-y-3">
            {c.missing_operations.map((mo, i) => (
              <div key={`mo-${mo.operation}-${i}`} className="bg-slate-900/60 border border-slate-800 rounded-md p-4">
                <div className="font-display font-medium text-slate-100">{mo.operation_label}</div>
                <div className="mt-2 grid grid-cols-1 md:grid-cols-3 gap-2">
                  {mo.alternatives.map((alt, j) => (
                    <div key={alt.machine_id || `${mo.operation}-alt-${j}`} className="px-3 py-2 rounded bg-slate-950 border border-slate-800">
                      <div className="text-sm text-slate-100">{alt.machine_name}</div>
                      <div className="text-xs text-slate-500 mt-1 font-mono-data">€{alt.hourly_rate}/h · {t("quoteTabs.estimateLabel")} {formatMoney(alt.estimated_external_cost_eur, q.currency)}</div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
